import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

export type Track = {
  id: string;
  name: string;
  artists: string;
  album: string;
  albumImage: string | null;
  uri: string;
};

export function SongPicker({
  value,
  onChange,
}: {
  value: Track | null;
  onChange: (track: Track | null) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Track[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  // Debounced search — waits for a pause in typing before hitting the server.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const handle = setTimeout(async () => {
      try {
        const { tracks } = await api.get<{ tracks: Track[] }>(
          `/spotify/search?q=${encodeURIComponent(q)}`,
        );
        setResults(tracks);
        setOpen(true);
      } catch {
        setError('Song search is unavailable right now.');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [query]);

  // Close the dropdown when clicking away.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const select = (track: Track) => {
    onChange(track);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  if (value) {
    return (
      <div className="flex items-center gap-3 border border-pine/30 p-3 font-sans">
        <Cover src={value.albumImage} size="h-12 w-12" />
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-pine">{value.name}</p>
          <p className="truncate text-base text-pine/60">{value.artists}</p>
        </div>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="shrink-0 text-sm uppercase tracking-wider text-midnight-teal hover:text-pine"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div ref={boxRef} className="relative font-sans">
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        placeholder="Search a song…"
        className="w-full border-b border-pine/30 bg-transparent py-2 focus:border-midnight-teal focus:outline-none"
      />
      {loading && <p className="mt-1 text-sm text-pine/50">Searching…</p>}
      {error && <p className="mt-1 text-sm text-dusty-rose">{error}</p>}

      {open && results.length > 0 && (
        <ul className="absolute z-20 mt-1 max-h-72 w-full overflow-auto border border-pine/20 bg-cream shadow-lg">
          {results.map((track) => (
            <li key={track.id}>
              <button
                type="button"
                onClick={() => select(track)}
                className="flex w-full items-center gap-3 p-2 text-left hover:bg-pine/5"
              >
                <Cover src={track.albumImage} size="h-10 w-10" />
                <span className="min-w-0">
                  <span className="block truncate text-pine">{track.name}</span>
                  <span className="block truncate text-base text-pine/60">
                    {track.artists}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Cover({ src, size }: { src: string | null; size: string }) {
  if (!src) return <div className={`${size} shrink-0 bg-pine/10`} />;
  return <img src={src} alt="" className={`${size} shrink-0 object-cover`} />;
}
